import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LiveCreateModal } from './LiveCreateModal'
import { AccountSwitcher } from './AccountSwitcher'
import { Button } from '../../components/ui/Button'
import { useWorkspaceAccount } from '../WorkspaceAccountProvider'
import { copyText, moneyAmount } from '../safe-ui'
import { useAppPath } from '../nav'
import { accountModeBadge } from '../account-badges'

function CredentialsMenu({ login, server }: { login: string; server: string }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState('')
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  async function onCopy(kind: 'login' | 'server', value: string) {
    const ok = await copyText(value)
    if (!ok) return
    setCopied(kind)
    window.setTimeout(() => setCopied(''), 1400)
  }

  return (
    <div className="cp-ac__more" ref={root}>
      <button
        type="button"
        className="cp-ac__more-btn"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Copy credentials"
        onClick={() => setOpen((v) => !v)}
      >
        ···
      </button>
      {open ? (
        <div className="cp-ac__more-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            disabled={!login}
            onClick={() => void onCopy('login', login)}
          >
            {copied === 'login' ? 'Login copied' : 'Copy Login'}
          </button>
          <button
            type="button"
            role="menuitem"
            disabled={!server}
            onClick={() => void onCopy('server', server)}
          >
            {copied === 'server' ? 'Server copied' : 'Copy Server'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

function PracticeStrip({
  active,
  balance,
  currency,
  mt5Login,
  onManage,
  onCreate,
  creating,
}: {
  active: boolean
  balance?: number | string | null
  currency?: string
  mt5Login?: number | string | null
  onManage?: () => void
  onCreate?: () => void
  creating?: boolean
}) {
  return (
    <aside className="cp-ac__practice" aria-label="Practice account">
      <div className="cp-ac__practice-main">
        <span className="cp-badge cp-badge--demo">Demo</span>
        <div>
          <strong>Practice Account</strong>
          {active ? (
            <p className="cp-muted">
              Active
              {mt5Login != null && String(mt5Login).trim() ? ` · MT5 ${mt5Login}` : ''}
              {' · '}
              Virtual Balance {moneyAmount(balance)} {currency || 'USD'}
            </p>
          ) : (
            <p className="cp-muted">Optional · Virtual Balance 1,000 USD</p>
          )}
        </div>
      </div>
      {active && onManage ? (
        <Button type="button" variant="ghost" size="sm" onClick={onManage}>
          Practice Account Details
        </Button>
      ) : onCreate ? (
        <Button type="button" variant="ghost" size="sm" disabled={creating} onClick={onCreate}>
          {creating ? 'Creating…' : 'Open Practice Account'}
        </Button>
      ) : null}
    </aside>
  )
}

/** LIVE / DEMO tabs when Live does not exist yet — LIVE shows open CTA, DEMO shows practice detail. */
function PendingLiveSwitcher({
  view,
  hasDemo,
  onLive,
  onDemo,
}: {
  view: 'live' | 'demo'
  hasDemo: boolean
  onLive: () => void
  onDemo: () => void
}) {
  return (
    <div className="cp-account-switcher" role="group" aria-label="Account mode">
      <button type="button" className={view === 'live' ? 'is-active' : ''} onClick={onLive}>
        LIVE
      </button>
      <button
        type="button"
        className={view === 'demo' ? 'is-active' : ''}
        onClick={onDemo}
        disabled={!hasDemo}
        title={!hasDemo ? 'No Demo account yet' : undefined}
      >
        DEMO
      </button>
    </div>
  )
}

type SelectedProps = {
  title: string
  status: string
  statusClass: string
  mt5Login: number | string | null | undefined
  server: string
  balance: number | string | null | undefined
  equity: number | string | null | undefined
  floating: number
  currency: string
  openCount: number
  pendingCount: number
  liveActions?: boolean
  depositsPath?: string
  withdrawalsPath?: string
}

function SelectedAccountBlock(props: SelectedProps) {
  const floatingNeg = Number(props.floating) < 0
  const login = String(props.mt5Login ?? '')
  const isLive = props.title === 'Live'
  const heading = isLive ? 'Live Trading Account' : props.title === 'Demo' ? 'Practice Account' : props.title

  return (
    <section className="cp-live-status cp-ac__status" aria-live="polite">
      <div className="cp-live-status__head">
        <span className={props.statusClass}>{props.status}</span>
        <h2 className="cp-ac__status-title">{heading}</h2>
        <div className="cp-ac__status-menu">
          <CredentialsMenu login={login} server={props.server} />
        </div>
      </div>

      <dl className="cp-live-status__metrics">
        <div>
          <dt>MT5 Login</dt>
          <dd className="cp-num">{props.mt5Login ?? '—'}</dd>
        </div>
        <div>
          <dt>Trading Server</dt>
          <dd>{props.server || '—'}</dd>
        </div>
        <div>
          <dt>Balance</dt>
          <dd className="cp-num">
            {moneyAmount(props.balance)} {props.currency}
          </dd>
        </div>
        <div>
          <dt>Equity</dt>
          <dd className="cp-num">
            {moneyAmount(props.equity)} {props.currency}
          </dd>
        </div>
      </dl>

      <dl className="cp-ac__status-extra" aria-label="Trading activity">
        <div>
          <dt>Floating P/L</dt>
          <dd className={`cp-num ${floatingNeg ? 'is-neg' : 'is-pos'}`}>
            {Number(props.floating).toFixed(2)}
          </dd>
        </div>
        <div>
          <dt>Open Positions</dt>
          <dd className="cp-num">{props.openCount}</dd>
        </div>
        <div>
          <dt>Pending Orders</dt>
          <dd className="cp-num">{props.pendingCount}</dd>
        </div>
      </dl>

      {props.liveActions && props.depositsPath && props.withdrawalsPath ? (
        <div className="cp-ac__actions" aria-label="Account actions">
          <div className="cp-ac__actions-row">
            <Button to={props.depositsPath} variant="primary">
              Deposit Funds
            </Button>
            <Button to={props.withdrawalsPath} variant="secondary">
              Withdraw Funds
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export function AccountsPageContent() {
  const appPath = useAppPath()
  const ws = useWorkspaceAccount()
  const [searchParams] = useSearchParams()
  const [liveModalOpen, setLiveModalOpen] = useState(false)
  const [appliedMode, setAppliedMode] = useState(false)
  /** When Live is absent: 'live' = open-Live hero, 'demo' = Manage Demo detail. */
  const [pendingView, setPendingView] = useState<'live' | 'demo'>('live')
  const [demoOpenMobile, setDemoOpenMobile] = useState(false)
  const [howOpen, setHowOpen] = useState(false)

  const live = ws.accounts.find((a) => {
    const m = String(a.mode || '').toUpperCase()
    return m === 'LIVE' || m === 'REAL'
  })
  const demo = ws.accounts.find((a) => String(a.mode || '').toUpperCase() === 'DEMO')
  const isLiveSelected = ws.mode === 'LIVE' || ws.mode === 'REAL'
  const hasLive = Boolean(live)

  useEffect(() => {
    if (ws.loading || appliedMode) return
    const mode = (searchParams.get('mode') || '').toLowerCase()
    if (mode === 'demo' && demo) {
      ws.setAccountId(demo.id)
      setPendingView('demo')
      setAppliedMode(true)
    } else if (mode === 'live' && live) {
      ws.setAccountId(live.id)
      setPendingView('live')
      setAppliedMode(true)
    } else if (!mode && live) {
      ws.setAccountId(live.id)
      setAppliedMode(true)
    } else if (!mode && demo) {
      ws.setAccountId(demo.id)
      setPendingView('demo')
      setAppliedMode(true)
    } else {
      setAppliedMode(true)
    }
  }, [ws.loading, demo?.id, live?.id, searchParams, appliedMode, ws.setAccountId])

  function manageDemo() {
    if (!demo) return
    ws.setAccountId(demo.id)
    setPendingView('demo')
  }

  if (ws.loading) {
    return (
      <div className="cp-ac">
        <div className="cp-ac__switcher-row">
          <div className="cp-account-switcher" role="group" aria-label="Account mode">
            <button type="button" className="is-active" disabled aria-busy="true">
              LIVE
            </button>
            <button type="button" disabled>
              DEMO
            </button>
          </div>
        </div>
        <p className="cp-muted ap-inline-status" aria-live="polite">
          Loading trading accounts…
        </p>
        <section className="cp-live-status cp-ac__status is-refreshing" aria-busy="true">
          <div className="cp-live-status__head">
            <h2 className="cp-ac__status-title">Trading Account</h2>
          </div>
          <p className="cp-muted">Fetching balance and positions from MT5…</p>
        </section>
      </div>
    )
  }

  const liveCreateModal = (
    <LiveCreateModal
      open={liveModalOpen}
      onClose={() => setLiveModalOpen(false)}
      onCreate={async (p, c) => ws.requestLiveAccount(p, c)}
    />
  )

  // —— No Live: Live hero (90%) or Demo detail after Manage Demo ——
  if (!hasLive) {
    return (
      <div className="cp-ac">
        {ws.error ? (
          <p className="cp-banner cp-banner--error" role="alert">
            {ws.error}
          </p>
        ) : null}

        <div className="cp-ac__switcher-row">
          <PendingLiveSwitcher
            view={pendingView}
            hasDemo={Boolean(demo)}
            onLive={() => setPendingView('live')}
            onDemo={() => {
              if (demo) {
                ws.setAccountId(demo.id)
                setPendingView('demo')
              }
            }}
          />
        </div>

        {pendingView === 'live' ? (
          <>
            <section className="cp-live-hero cp-live-hero--facts" aria-labelledby="ac-live-hero-title">
              <div className="cp-live-hero__copy">
                <span className="cp-badge cp-badge--live">Live Account</span>
                <h2 id="ac-live-hero-title">Start your real trading journey.</h2>
                <p>Open a Live MetaTrader 5 account and fund it securely to begin trading.</p>
                <div className="cp-live-hero__actions">
                  <Button type="button" variant="primary" onClick={() => setLiveModalOpen(true)}>
                    Open Live Account
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setHowOpen((v) => !v)}>
                    How it works
                  </Button>
                </div>
                {howOpen ? (
                  <ol className="cp-ac__how" aria-label="How Live account works">
                    <li>Create your Live MT5 login with a secure trading password.</li>
                    <li>Opening balance is 0 USD — deposit real funds next.</li>
                    <li>Launch MetaTrader 5 with server iBull Capital - Live.</li>
                  </ol>
                ) : null}
              </div>
              <dl className="cp-live-hero__facts" aria-label="Live account status">
                <div>
                  <dt>Live Account</dt>
                  <dd>Not Created</dd>
                </div>
                <div>
                  <dt>Platform</dt>
                  <dd>MetaTrader 5</dd>
                </div>
                <div>
                  <dt>Opening Balance</dt>
                  <dd className="cp-num">0 USD</dd>
                </div>
                <div>
                  <dt>Trading Server</dt>
                  <dd>iBull Capital - Live</dd>
                </div>
              </dl>
            </section>

            {demo ? (
              <PracticeStrip
                active
                balance={demo.balance}
                currency={demo.currency}
                mt5Login={demo.mt5Login}
                onManage={manageDemo}
              />
            ) : (
              <PracticeStrip
                active={false}
                onCreate={() => void ws.requestDemoAccount()}
                creating={ws.creatingAccount}
              />
            )}
          </>
        ) : demo ? (
          <SelectedAccountBlock
            title="Demo"
            status={String(demo.status || ws.status || 'Active')}
            statusClass={accountModeBadge('DEMO', demo.status)}
            mt5Login={demo.mt5Login ?? ws.mt5Login}
            server={ws.server}
            balance={ws.account?.id === demo.id ? ws.balance : demo.balance}
            equity={ws.account?.id === demo.id ? ws.equity : demo.equity}
            floating={ws.account?.id === demo.id ? ws.floating : Number(demo.floating_profit ?? 0)}
            currency={demo.currency || ws.currency || 'USD'}
            openCount={ws.account?.id === demo.id ? ws.openCount : 0}
            pendingCount={ws.account?.id === demo.id ? ws.pendingCount : 0}
          />
        ) : null}

        {liveCreateModal}
      </div>
    )
  }

  // —— Live exists ——
  return (
    <div className="cp-ac">
      <div className="cp-ac__switcher-row">
        <AccountSwitcher alwaysShow />
        {ws.refreshing ? (
          <span className="cp-muted cp-ac__sync" role="status">
            Updating…
          </span>
        ) : null}
      </div>

      {ws.error ? (
        <p className="cp-banner cp-banner--error" role="alert">
          {ws.error}
        </p>
      ) : null}

      {isLiveSelected && live ? (
        <SelectedAccountBlock
          title="Live"
          status={ws.status}
          statusClass={accountModeBadge(ws.mode, ws.statusRaw)}
          mt5Login={ws.mt5Login}
          server={ws.server}
          balance={ws.balance}
          equity={ws.equity}
          floating={ws.floating}
          currency={ws.currency}
          openCount={ws.openCount}
          pendingCount={ws.pendingCount}
          liveActions
          depositsPath={appPath('/app/deposits')}
          withdrawalsPath={appPath('/app/withdrawals')}
        />
      ) : demo ? (
        <SelectedAccountBlock
          title="Demo"
          status={ws.status}
          statusClass={accountModeBadge(ws.mode, ws.statusRaw)}
          mt5Login={ws.mt5Login}
          server={ws.server}
          balance={ws.balance}
          equity={ws.equity}
          floating={ws.floating}
          currency={ws.currency}
          openCount={ws.openCount}
          pendingCount={ws.pendingCount}
        />
      ) : null}

      {isLiveSelected ? (
        <>
          <button
            type="button"
            className="cp-ac__practice-toggle"
            aria-expanded={demoOpenMobile}
            onClick={() => setDemoOpenMobile((v) => !v)}
          >
            Practice Account
            <span aria-hidden="true">{demoOpenMobile ? '−' : '+'}</span>
          </button>
          <div className={`cp-ac__practice-wrap${demoOpenMobile ? ' is-open' : ''}`}>
            {demo ? (
              <PracticeStrip
                active
                balance={demo.balance}
                currency={demo.currency}
                mt5Login={demo.mt5Login}
                onManage={manageDemo}
              />
            ) : (
              <PracticeStrip
                active={false}
                onCreate={() => void ws.requestDemoAccount()}
                creating={ws.creatingAccount}
              />
            )}
          </div>
        </>
      ) : null}

      {liveCreateModal}
    </div>
  )
}
